import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format, addWeeks, subWeeks, startOfWeek, addDays } from 'date-fns';
import { LOADING, SUCCESS } from '@constants/requestPhase';
import {
  getEmployees,
  getRoles,
  getWeekSchedule,
  createShift,
  updateShift,
  deleteShift,
  publishWeek,
  getTimeOffRequests,
  reviewTimeOffRequest,
  getShiftTrades,
  reviewShiftTrade,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  createRole,
  updateRole,
  deleteRole,
} from '@store/scheduling/duck';
import SchedulingView from '@components/views/Scheduling';

const getWeekStart = (date) => startOfWeek(date, { weekStartsOn: 1 }); // Monday

const Scheduling = () => {
  const dispatch = useDispatch();
  const {
    roles,
    employees,
    shifts,
    reservationCounts,
    timeOffRequests,
    shiftTrades,
    getWeekSchedulePhase,
    createShiftPhase,
    publishWeekPhase,
  } = useSelector(state => state.scheduling);

  const [currentWeekStart, setCurrentWeekStart] = useState(() => getWeekStart(new Date()));
  const [shiftModalOpen, setShiftModalOpen] = useState(false);
  const [editingShift, setEditingShift] = useState(null); // null = create, object = edit
  const [shiftFormData, setShiftFormData] = useState({
    employee_id: '',
    role_id: '',
    shift_date: '',
    start_time: '17:00',
    end_time: '23:00',
    break_duration_mins: 30,
    notes: '',
  });
  const [activeTab, setActiveTab] = useState('schedule'); // 'schedule' | 'timeoff' | 'trades'
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);

  // Employee modal
  const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [employeeForm, setEmployeeForm] = useState({ first_name: '', last_name: '', phone: '', email: '', wage: '', wage_type: 'hourly', role_ids: [], primary_role_id: '' });

  // Role modal
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: '', department: '', color: '#e85d5d', hourly_rate: '' });

  const weekStartStr = format(currentWeekStart, 'yyyy-MM-dd');
  const weekEndStr = format(addDays(currentWeekStart, 6), 'yyyy-MM-dd');

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));

  const draftCount = shifts.filter(s => s.status === 'draft').length;

  // ─── Load data ────────────────────────────────────────────────────────────

  useEffect(() => {
    dispatch(getRoles());
    dispatch(getEmployees());
    dispatch(getTimeOffRequests());
    dispatch(getShiftTrades());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getWeekSchedule(weekStartStr));
  }, [dispatch, weekStartStr]);

  // ─── Navigation ───────────────────────────────────────────────────────────

  const handlePrevWeek = useCallback(() => {
    setCurrentWeekStart(prev => subWeeks(prev, 1));
  }, []);

  const handleNextWeek = useCallback(() => {
    setCurrentWeekStart(prev => addWeeks(prev, 1));
  }, []);

  const handleGoToToday = useCallback(() => {
    setCurrentWeekStart(getWeekStart(new Date()));
  }, []);

  // ─── Shift modal ──────────────────────────────────────────────────────────

  const handleOpenCreateShift = useCallback((prefillDate = '', prefillEmployeeId = '') => {
    setEditingShift(null);
    setShiftFormData({
      employee_id: prefillEmployeeId || (employees[0]?.id || ''),
      role_id: '',
      shift_date: prefillDate || weekStartStr,
      start_time: '17:00',
      end_time: '23:00',
      break_duration_mins: 30,
      notes: '',
    });
    setShiftModalOpen(true);
  }, [employees, weekStartStr]);

  const handleOpenEditShift = useCallback((shift) => {
    setEditingShift(shift);
    setShiftFormData({
      employee_id: shift.employee_id,
      role_id: shift.role_id,
      shift_date: shift.shift_date,
      start_time: shift.start_time.slice(0, 5),
      end_time: shift.end_time.slice(0, 5),
      break_duration_mins: shift.break_duration_mins,
      notes: shift.notes || '',
    });
    setShiftModalOpen(true);
  }, []);

  const handleCloseShiftModal = useCallback(() => {
    setShiftModalOpen(false);
    setEditingShift(null);
  }, []);

  const handleShiftFormChange = useCallback((field, value) => {
    setShiftFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSaveShift = useCallback(async () => {
    if (editingShift) {
      await dispatch(updateShift({ id: editingShift.id, ...shiftFormData }));
    } else {
      await dispatch(createShift(shiftFormData));
    }
    setShiftModalOpen(false);
  }, [dispatch, editingShift, shiftFormData]);

  const handleDeleteShift = useCallback(async (shiftId) => {
    await dispatch(deleteShift({ id: shiftId }));
    setShiftModalOpen(false);
  }, [dispatch]);

  // ─── Publish ──────────────────────────────────────────────────────────────

  const handlePublishWeek = useCallback(async () => {
    await dispatch(publishWeek({ week_start: weekStartStr, week_end: weekEndStr, notify: true }));
    setPublishConfirmOpen(false);
  }, [dispatch, weekStartStr, weekEndStr]);

  // ─── Time-off ─────────────────────────────────────────────────────────────

  const handleReviewTimeOff = useCallback(async (id, status, reviewedById) => {
    await dispatch(reviewTimeOffRequest({ id, status, reviewed_by_id: reviewedById }));
  }, [dispatch]);

  // ─── Trades ───────────────────────────────────────────────────────────────

  const handleReviewTrade = useCallback(async (id, status, managerId) => {
    await dispatch(reviewShiftTrade({ id, status, manager_reviewed_by_id: managerId }));
  }, [dispatch]);

  // ─── Employee modal ───────────────────────────────────────────────────────

  const handleOpenEmployeeModal = useCallback((employee = null) => {
    if (employee) {
      setEditingEmployee(employee);
      setEmployeeForm({
        first_name: employee.first_name || '',
        last_name: employee.last_name || '',
        phone: employee.phone || '',
        email: employee.email || '',
        wage: employee.wage || '',
        wage_type: employee.wage_type || 'hourly',
        role_ids: employee.employee_roles?.map(r => r.role_id) || [],
        primary_role_id: employee.employee_roles?.find(r => r.is_primary)?.role_id || '',
      });
    } else {
      setEditingEmployee(null);
      setEmployeeForm({ first_name: '', last_name: '', phone: '', email: '', wage: '', wage_type: 'hourly', role_ids: [], primary_role_id: '' });
    }
    setEmployeeModalOpen(true);
  }, []);

  const handleSaveEmployee = useCallback(async () => {
    const payload = {
      ...employeeForm,
      wage: employeeForm.wage === '' ? null : Number(employeeForm.wage),
      primary_role_id: employeeForm.primary_role_id || undefined,
    };
    if (editingEmployee) {
      await dispatch(updateEmployee({ id: editingEmployee.id, ...payload }));
    } else {
      await dispatch(createEmployee(payload));
    }
    setEmployeeModalOpen(false);
    dispatch(getEmployees());
  }, [dispatch, employeeForm, editingEmployee]);

  const handleDeleteEmployee = useCallback(async (id) => {
    if (!window.confirm('Remove this employee?')) return;
    await dispatch(deleteEmployee({ id }));
    dispatch(getEmployees());
  }, [dispatch]);

  // ─── Role modal ────────────────────────────────────────────────────────────

  const handleOpenRoleModal = useCallback((role = null) => {
    if (role) {
      setEditingRole(role);
      setRoleForm({ name: role.name, department: role.department || '', color: role.color || '#e85d5d', hourly_rate: role.hourly_rate || '' });
    } else {
      setEditingRole(null);
      setRoleForm({ name: '', department: '', color: '#e85d5d', hourly_rate: '' });
    }
    setRoleModalOpen(true);
  }, []);

  const handleSaveRole = useCallback(async () => {
    const payload = { ...roleForm, hourly_rate: roleForm.hourly_rate === '' ? null : Number(roleForm.hourly_rate) };
    if (editingRole) {
      await dispatch(updateRole({ id: editingRole.id, ...payload }));
    } else {
      await dispatch(createRole(payload));
    }
    setRoleModalOpen(false);
    dispatch(getRoles());
  }, [dispatch, roleForm, editingRole]);

  const handleDeleteRole = useCallback(async (id) => {
    if (!window.confirm('Delete this role?')) return;
    await dispatch(deleteRole({ id }));
    dispatch(getRoles());
  }, [dispatch]);

  // ─── Staffing suggestion ──────────────────────────────────────────────────
  // Simple heuristic: 1 staff per 20 covers. Flag days where scheduled staff < suggested.

  const getStaffingSuggestion = useCallback((dateStr) => {
    const covers = reservationCounts[dateStr] || 0;
    const suggested = Math.ceil(covers / 20);
    const scheduled = shifts.filter(
      s => s.shift_date === dateStr && !['cancelled', 'no_show'].includes(s.status),
    ).length;
    return { covers, suggested, scheduled, understaffed: suggested > 0 && scheduled < suggested };
  }, [reservationCounts, shifts]);

  return (
    <SchedulingView
      // Data
      roles={roles}
      employees={employees}
      shifts={shifts}
      weekDays={weekDays}
      weekStartStr={weekStartStr}
      weekEndStr={weekEndStr}
      reservationCounts={reservationCounts}
      timeOffRequests={timeOffRequests}
      shiftTrades={shiftTrades}
      draftCount={draftCount}
      activeTab={activeTab}
      // Loading states
      loading={getWeekSchedulePhase === LOADING}
      saving={createShiftPhase === LOADING}
      publishing={publishWeekPhase === LOADING}
      // Shift modal
      shiftModalOpen={shiftModalOpen}
      editingShift={editingShift}
      shiftFormData={shiftFormData}
      // Publish confirm
      publishConfirmOpen={publishConfirmOpen}
      // Handlers
      onPrevWeek={handlePrevWeek}
      onNextWeek={handleNextWeek}
      onGoToToday={handleGoToToday}
      onOpenCreateShift={handleOpenCreateShift}
      onOpenEditShift={handleOpenEditShift}
      onCloseShiftModal={handleCloseShiftModal}
      onShiftFormChange={handleShiftFormChange}
      onSaveShift={handleSaveShift}
      onDeleteShift={handleDeleteShift}
      onOpenPublishConfirm={() => setPublishConfirmOpen(true)}
      onClosePublishConfirm={() => setPublishConfirmOpen(false)}
      onPublishWeek={handlePublishWeek}
      onSetActiveTab={setActiveTab}
      onReviewTimeOff={handleReviewTimeOff}
      onReviewTrade={handleReviewTrade}
      getStaffingSuggestion={getStaffingSuggestion}
      // Employee modal
      employeeModalOpen={employeeModalOpen}
      editingEmployee={editingEmployee}
      employeeForm={employeeForm}
      onOpenEmployeeModal={handleOpenEmployeeModal}
      onCloseEmployeeModal={() => setEmployeeModalOpen(false)}
      onEmployeeFormChange={(field, value) => setEmployeeForm(prev => ({ ...prev, [field]: value }))}
      onSaveEmployee={handleSaveEmployee}
      onDeleteEmployee={handleDeleteEmployee}
      // Role modal
      roleModalOpen={roleModalOpen}
      editingRole={editingRole}
      roleForm={roleForm}
      onOpenRoleModal={handleOpenRoleModal}
      onCloseRoleModal={() => setRoleModalOpen(false)}
      onRoleFormChange={(field, value) => setRoleForm(prev => ({ ...prev, [field]: value }))}
      onSaveRole={handleSaveRole}
      onDeleteRole={handleDeleteRole}
    />
  );
};

export default Scheduling;
